import { StockStatusEnum } from '@petsmart-ui/sparky';

import { getDerivedVariantOptions } from '@libs/algolia';
import type {
    DeliveryMethodType,
    Product,
    ProductApiResponse,
    TailoredApiData,
    VariantKey,
    VariantValues,
} from '@libs/common-types';
import { PDP_VARIANT_DISPLAY_ORDER } from '@libs/common-types';

import { validateStockStatus } from '../../../utils/stockStatusUtils';

type Combination = Record<string, string>;

export type VariantGroupOption = {
    key: string;
    value: string;
    display: string;
    isChecked: boolean;
    isUnavailable: boolean;
};

export type VariantGroup = {
    name: VariantKey;
    display: string;
    variants: VariantGroupOption[];
};

type FindSkuForVariantChangeProps = {
    activeVariants: Combination;
    group: VariantKey;
    isSkuAvailable?: (sku: string) => boolean;
    value: string;
    variantToSkuMap: Record<string, string>;
};

type IsVariantInStockProps = {
    sku: string | null;
    product: Product | undefined;
    method: DeliveryMethodType;
    tailored: TailoredApiData | undefined;
};

type MapVariantGroupsProps = {
    activeVariants: VariantValues | null | undefined;
    method: DeliveryMethodType;
    tailored: TailoredApiData | null | undefined;
    uniqueVariantValues: ProductApiResponse['data']['uniqueVariantValues'] | undefined;
    variantToSkuMap: ProductApiResponse['data']['variantToSkuMap'] | undefined;
    variants: Record<string, Product>;
};

export const parseCombinationKey = (combinationKey: string): Combination => {
    const combination: Combination = {};

    combinationKey.split(';').forEach(pair => {
        const [type, value] = pair.split(':');
        if (type && value) {
            combination[type] = value;
        }
    });

    return combination;
};

const getSelectableVariantOptions = (combinations: Combination[]): VariantKey[] => {
    const derivedOptions = getDerivedVariantOptions(combinations);
    return PDP_VARIANT_DISPLAY_ORDER.filter(
        option => !derivedOptions.has(option) && combinations.some(combination => Boolean(combination[option]))
    );
};

/**
 * Finds a valid SKU without changing any higher-priority picker. Among valid
 * candidates it preserves current values, then prefers stock, then catalog order.
 */
export const findSkuForVariantChange = ({
    activeVariants,
    group,
    isSkuAvailable,
    value,
    variantToSkuMap,
}: FindSkuForVariantChangeProps): string | null => {
    const candidates = Object.entries(variantToSkuMap).map(([key, sku]) => ({
        combination: parseCombinationKey(key),
        sku,
    }));
    const selectableOptions = getSelectableVariantOptions(candidates.map(candidate => candidate.combination));
    const changedOptionIndex = selectableOptions.indexOf(group);
    if (changedOptionIndex < 0) return null;

    const higherPriorityOptions = selectableOptions.slice(0, changedOptionIndex);
    let bestMatch: { sku: string; score: number; isAvailable: boolean } | null = null;

    for (const candidate of candidates) {
        if (
            candidate.combination[group] !== value ||
            higherPriorityOptions.some(option => candidate.combination[option] !== activeVariants[option])
        ) {
            continue;
        }

        const score = Object.entries(activeVariants).filter(
            ([name, selectedValue]) => name !== group && candidate.combination[name] === selectedValue
        ).length;
        const isAvailable = isSkuAvailable?.(candidate.sku) ?? false;
        if (
            !bestMatch ||
            score > bestMatch.score ||
            (score === bestMatch.score && isAvailable && !bestMatch.isAvailable)
        ) {
            bestMatch = { sku: candidate.sku, score, isAvailable };
        }
    }

    return bestMatch?.sku ?? null;
};

export const isVariantInStock = ({ sku, product, method, tailored }: IsVariantInStockProps): boolean => {
    if (!sku) return false;

    const tailoredFulfillmentDetails = tailored?.variants[sku]?.[method];
    if (!tailoredFulfillmentDetails) return true;

    const availableQuantity =
        'availableStock' in tailoredFulfillmentDetails && typeof tailoredFulfillmentDetails.availableStock === 'number'
            ? tailoredFulfillmentDetails.availableStock
            : Infinity;
    const minimumQuantity = product?.deliveryMethods[method]?.purchaseQuantities?.min ?? 1;

    const stockStatus = validateStockStatus({
        stockQuantity: availableQuantity,
        minimumCaseQty: minimumQuantity,
        customPharmacyOOS: Boolean(product?.pharmacy?.oos),
    });

    return stockStatus === StockStatusEnum.InStock || stockStatus === StockStatusEnum.FewLeftInStock;
};

export const mapVariantGroups = ({
    activeVariants,
    method,
    tailored,
    uniqueVariantValues,
    variantToSkuMap,
    variants,
}: MapVariantGroupsProps): VariantGroup[] => {
    if (!uniqueVariantValues) return [];

    const selectedValues = activeVariants ?? {};
    const resolvedVariantToSkuMap = variantToSkuMap ?? {};
    const combinations = Object.keys(resolvedVariantToSkuMap).map(parseCombinationKey);
    const derivedOptions = getDerivedVariantOptions(combinations);
    const getActiveVariantDisplay = (variantGroup: VariantKey) => {
        const variant = uniqueVariantValues[variantGroup]?.find(value => value.value === selectedValues[variantGroup]);
        return variant?.display || variant?.value || '';
    };
    const isInStock = (sku: string | null) =>
        isVariantInStock({
            sku,
            product: sku ? variants[sku] : undefined,
            method,
            tailored: tailored ?? undefined,
        });

    return (Object.keys(uniqueVariantValues) as VariantKey[]).map(name => {
        const values = uniqueVariantValues[name] ?? [];
        const isDerived = derivedOptions.has(name);

        return {
            name,
            display: getActiveVariantDisplay(name),
            variants: isDerived
                ? []
                : values
                      .map(value => {
                          const sku = findSkuForVariantChange({
                              activeVariants: selectedValues,
                              group: name,
                              isSkuAvailable: sku => isInStock(sku),
                              value: value.value,
                              variantToSkuMap: resolvedVariantToSkuMap,
                          });
                          if (!sku) return null;

                          return {
                              key: value.key,
                              value: value.value,
                              display: value.display ?? value.value,
                              isChecked: selectedValues[name] === value.value,
                              isUnavailable: !isInStock(sku),
                          };
                      })
                      .filter((value): value is VariantGroupOption => value !== null),
        };
    });
};
